/**
 * What an emailed link should tell the person once they land. Today that is the two email-change
 * landings, which said nothing at all.
 *
 *   Halfway — secure email change needs both the current and the new inbox. Opening the first link
 *     sends the person back with only GoTrue's message in the hash ("Confirmation link accepted.
 *     Please proceed to confirm link sent to the other email"). Nothing read it, so the address had
 *     not changed and the screen gave no sign of why.
 *   Done — the second link carries the session for the new address. The landing guard used to keep
 *     the session already in storage because it is the same account, which left the OLD address on
 *     screen for up to an hour; it now lets this one through, and the app says the change took.
 *
 * The guard runs before the Supabase client exists, so it only records which landing this was (in
 * sessionStorage, for this tab), and the app shows the words once it can read the account. Pure
 * apart from the storage helpers, so the rules run under `node --test`.
 */

export const LANDING_NOTICE_KEY = 'arcos.authLandingNotice';
export const EMAIL_CHANGE_HALF = 'email-change-half';
export const EMAIL_CHANGED = 'email-changed';

/** The start of GoTrue's message for "one of the two inboxes confirmed" (auth `internal/api/verify.go`). */
const HALF_CONFIRMED = 'Confirmation link accepted';

/** Did this landing come from the first of the two email-change links? */
export function isHalfConfirmedEmailChange(loc) {
  const hash = new URLSearchParams((loc?.hash || '').replace(/^#/, ''));
  return String(hash.get('message') || '').startsWith(HALF_CONFIRMED);
}

export function recordLandingNotice(win, kind) {
  try { win.sessionStorage.setItem(LANDING_NOTICE_KEY, kind); } catch { /* storage off: no notice */ }
}

/**
 * The landing to explain. Reading does not clear it: the caller clears it once the words are on
 * screen (clearLandingNotice), because a read that cleared would lose the notice whenever the first
 * reader goes away before showing it — which React's development mode does on purpose, running every
 * effect twice and throwing the first run's result away.
 */
export function peekLandingNotice(win = globalThis) {
  try { return win.sessionStorage?.getItem(LANDING_NOTICE_KEY) || null; } catch { return null; }
}

/** Said once: cleared when shown, so a reload does not say it again. */
export function clearLandingNotice(win = globalThis) {
  try { win.sessionStorage?.removeItem(LANDING_NOTICE_KEY); } catch { /* storage off */ }
}

/**
 * The words for a landing. `email` is the address the account signs in with now and `newEmail` the
 * one still waiting (Supabase's `user.new_email`); either may be missing, for instance when the link
 * was opened on another device that is not signed in, and the sentence holds without them.
 */
export function landingNoticeText(kind, { email = null, newEmail = null } = {}) {
  if (kind === EMAIL_CHANGE_HALF) {
    return email && newEmail
      ? `One of two confirmed. Open the link in the other email too: one went to ${email} and one to ${newEmail}. Your sign-in email changes once both are confirmed.`
      : 'One of two confirmed. Open the link in the other email too. Your sign-in email changes once both are confirmed.';
  }
  if (kind === EMAIL_CHANGED) {
    return email ? `Your sign-in email is now ${email}.` : 'Your sign-in email has changed.';
  }
  return null;
}
